import React, { useEffect, useState } from "react";
import { Avatar, Box, Divider, ListItemIcon, Menu, MenuItem, Typography } from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LogoutIcon from "@mui/icons-material/Logout";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Helpers/Contexts/AuthContext";
import { supabase } from "../../supabaseClient";

// Avatar vindo do metadata do usuário ou das identities do Google
function getAuthAvatarUrl(user) {
  const identityWithAvatar = user?.identities?.find(id => id.identity_data?.avatar_url || id.identity_data?.picture);
  return (
    user?.user_metadata?.avatar_url ||
    user?.user_metadata?.picture ||
    user?.user_metadata?.photo_url ||
    user?.user_metadata?.photoURL ||
    user?.user_metadata?.avatar ||
    identityWithAvatar?.identity_data?.avatar_url ||
    identityWithAvatar?.identity_data?.picture ||
    user?.identities?.[0]?.identity_data?.avatar_url ||
    user?.identities?.[0]?.identity_data?.picture ||
    ""
  );
}

export default function NavUser({ compact = false }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [profileAvatar, setProfileAvatar] = useState("");

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleConfig = () => {
    handleClose();
  };

  const handleLogout = async () => {
    handleClose();
    if (signOut) {
      await signOut();
    }
    navigate("/");
  };

  useEffect(() => {
    if (!user?.id) return;

    const authAvatar = getAuthAvatarUrl(user);
    if (authAvatar) {
      setProfileAvatar(authAvatar);
      return;
    }

    // Fallback: busca da tabela de perfis caso configurada no banco
    let cancelled = false;
    async function fetchDbProfile() {
      try {
        const { data } = await supabase
          .from('user_profiles')
          .select('avatar_url, picture, photo_url')
          .eq('id', user.id)
          .maybeSingle();

        if (data && !cancelled) {
          const url = data.avatar_url || data.picture || data.photo_url;
          if (url) setProfileAvatar(url);
        }
      } catch (err) {
        // Ignora silenciosamente
      }
    }
    fetchDbProfile();
    return () => { cancelled = true; };
  }, [user]);

  const userName = user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email?.split('@')[0] || "Usuário";
  const userEmail = user?.email || "usuario@empresa.com";
  const userAvatar = profileAvatar || getAuthAvatarUrl(user);

  return (
    <Box sx={{ p: compact ? 0 : 2 }}>
      <Box
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.2,
          p: compact ? 0.5 : 1,
          px: compact ? 1 : 1,
          borderRadius: 1.5,
          cursor: "pointer",
          transition: "background-color 0.2s ease",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          ...(open && {
            backgroundColor: "action.selected",
          }),
        }}
      >
        <Avatar
          src={userAvatar}
          alt={userName}
          variant="rounded"
          imgProps={{ referrerPolicy: "no-referrer" }}
          sx={{ width: 32, height: 32, borderRadius: 1.5 }}
        >
          {userName.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0, display: { xs: compact ? 'none' : 'flex', sm: 'flex' }, flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ color: "text.primary", fontWeight: 500, lineHeight: 1.1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", mb: 0.3 }}>
            {userName}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.1, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
            {userEmail}
          </Typography>
        </Box>
        <MoreVertIcon sx={{ fontSize: 18, color: "text.secondary", display: { xs: compact ? 'none' : 'block', sm: 'block' } }} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          elevation: 3,
          sx: {
            width: anchorEl ? Math.max(anchorEl.clientWidth, 200) : 220, // no celular o gatilho é só o avatar
            mt: 0.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: '0px 4px 20px rgba(0,0,0,0.1)',
            backgroundImage: 'none',
          }
        }}
      >
        <MenuItem onClick={handleConfig} sx={{ py: 1, px: 2, fontSize: 14 }}>
          <ListItemIcon sx={{ minWidth: 32 }}><AccountCircleIcon fontSize="small" /></ListItemIcon>
          Conta
        </MenuItem>
        <MenuItem onClick={handleClose} sx={{ py: 1, px: 2, fontSize: 14 }}>
          <ListItemIcon sx={{ minWidth: 32 }}><CreditCardIcon fontSize="small" /></ListItemIcon>
          Faturamento
        </MenuItem>
        <MenuItem onClick={handleClose} sx={{ py: 1, px: 2, fontSize: 14 }}>
          <ListItemIcon sx={{ minWidth: 32 }}><NotificationsIcon fontSize="small" /></ListItemIcon>
          Notificações
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        <MenuItem onClick={handleLogout} sx={{ py: 1, px: 2, fontSize: 14 }}>
          <ListItemIcon sx={{ minWidth: 32 }}><LogoutIcon fontSize="small" /></ListItemIcon>
          Sair
        </MenuItem>
      </Menu>
    </Box>
  );
}
